import type { OktaAuth } from "@okta/okta-auth-js";
import axios, { type AxiosInstance } from "axios";
import { AUTH_BYPASS, BYPASS_TOKEN } from "@/auth-bypass";
import { API_URL, CONTROL_PLANE_API_URL } from "@/constants/constants";

// Tenants API Endpoints
export const TENANTS_LIST = "/v1/admin_panel/tenants";
export const TENANT_ADMIN_USER = (tenantId: string) =>
	`/v1/admin_panel/users/${tenantId}`;

// Control Plane API Endpoints. Every one of them resolves the tenant from the
// `X-Tenant-Id` header rather than the path — see `tenantHeaders`.
export const TENANT = "/v1/tenant";
export const RULES = "/v1/rules";
export const FEATURES = "/v1/feature-definitions";
export const DEACTIVATE_FEATURE = (entity: string, key: string) =>
	`/v1/feature-definitions/${encodeURIComponent(entity)}/${encodeURIComponent(key)}/deactivate`;

const JSON_HEADERS = {
	accept: "application/json",
	"Content-Type": "application/json",
};

export const API = axios.create({
	baseURL: API_URL,
	headers: JSON_HEADERS,
});

export const CONTROL_PLANE_API = axios.create({
	baseURL: CONTROL_PLANE_API_URL,
	headers: JSON_HEADERS,
});

/** Envelope the control plane wraps around every successful response body. */
export type ControlPlaneResponse<T> = {
	data: T;
	ok: boolean;
};

/**
 * Scopes a control plane request to one tenant. Passed per request because the
 * admin panel moves between tenants within a session.
 */
export const tenantHeaders = (tenantId: string) => ({
	"X-Tenant-Id": tenantId,
});

const attachAuthorization = (instance: AxiosInstance, oktaAuth: OktaAuth) => {
	instance.interceptors.request.use(async (config) => {
		if (AUTH_BYPASS) {
			if (BYPASS_TOKEN) {
				config.headers.Authorization = `Bearer ${BYPASS_TOKEN}`;
			}
			return config;
		}

		const accessToken = oktaAuth.getAccessToken();
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	});
};

export const setupApiInterceptor = (oktaAuth: OktaAuth) => {
	// Both hosts sit behind the same Okta app, so they share one access token.
	attachAuthorization(API, oktaAuth);
	attachAuthorization(CONTROL_PLANE_API, oktaAuth);
};
